import { FC, lazy, Suspense, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ErrorText, FlexContent } from "@rothoblaas/design-system";
import Header, { HeaderPath } from "../../components/ui/Header";
import ReadOnlyGate from "../../components/ui/ReadOnlyGate";
import useI18n from "../../hooks/useTranslations";
import {
  CustomError,
  isCalculationModuleNotSupported,
  isCalculationReadNotAllowed,
  isNotFound,
} from "../../http/errors";
import { Calculation as Conf } from "../../http/types/calculations";
import { useCalculationsApi } from "../../http/useCalculations";
import { getEngine } from "../engines/registry";

// only for a calculation not shared with the user
const NotSharedPage = lazy(() => import("../../components/sharing/NotSharedPage"));

interface CalculationProps {
  isAdmin?: boolean;
  /** The header's title, the module's name by default (e.g. a single-module app's own name) */
  title?: string;
}

export interface UpdateCalculationFormBody {
  name: string;
  description?: string;
}

const Calculation: FC<CalculationProps> = ({ isAdmin = false, title }: CalculationProps) => {
  const i18n = useI18n();
  const { projectId } = useParams<{ projectId: string }>();
  const { calculationId } = useParams<{ calculationId: string }>();
  const [searchParams] = useSearchParams();
  const calculationsApi = useCalculationsApi();

  const projectIdFromQuery = searchParams.get("projectId") ?? undefined;
  const projectContextId = projectId ?? projectIdFromQuery;

  const listCalculationsUrl = isAdmin ? "/admin/calculations" : "/my-calculations";
  const listProjectsUrl = isAdmin ? `/admin/projects` : `/my-projects`;

  const aborter = useRef(new AbortController());
  const { error, data } = calculationsApi.useCalculation(calculationId!, aborter.current.signal, isAdmin);

  // Store variables in order to avoid the UI to rerender when mutating
  const [state, setState] = useState<{ data?: Conf; error?: CustomError }>();
  // Lets a module render its own controls into the header (e.g. the deck designer's unit toggle).
  const [headerButtonsContainer, setHeaderButtonsContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setState({
      data: data ?? state?.data,
      error: error ?? state?.error,
    });
  }, [data, error]);

  const path: HeaderPath[] = projectContextId
    ? [
        {
          url: `${listProjectsUrl}`,
          display: isAdmin ? i18n.translation.projects.all : i18n.translation.projects.my,
        },
        {
          url: `${listProjectsUrl}/${state?.data?.project?.id}?tab=1`,
          display: `${state?.data?.project?.name ?? ""}: ${i18n.translation.calculations.plural.toLowerCase()}`,
        },
      ]
    : [
        {
          url: `${listCalculationsUrl}`,
          display: isAdmin ? i18n.translation.calculations.all : i18n.translation.calculations.my,
        },
      ];

  path.push({
    display: state?.data?.name ?? "",
  });

  if (!state?.data && !state?.error) {
    return;
  }

  const engine = getEngine(state.data?.module.id);
  const ModuleRenderer = engine?.Calculator;

  const canEdit = state.data?.canEdit ?? false;

  return !state?.error ? (
    <ReadOnlyGate canEdit={canEdit}>
      <Header
        title={title ?? state.data?.module?.name ?? i18n.translation.common.loading}
        path={path}
        accentColor={state.data?.module?.category?.color}
        buttons={<div ref={setHeaderButtonsContainer} />}
      />
      {ModuleRenderer ? (
        // An engine's calculator may be loaded on demand (lazy).
        <Suspense fallback={null}>
          <ModuleRenderer
            data={state.data}
            isAdmin={isAdmin}
            readOnly={!canEdit}
            headerButtonsContainer={headerButtonsContainer}
          />
        </Suspense>
      ) : (
        <>
          <ErrorText text={i18n.translation.common.genericError} />
        </>
      )}
    </ReadOnlyGate>
  ) : !isAdmin && isCalculationReadNotAllowed(state.error) ? (
    <Suspense fallback={null}>
      <NotSharedPage resource="calculations" resourceId={calculationId!} />
    </Suspense>
  ) : (
    <>
      <Header
        title={isNotFound(error) ? i18n.translation.common.notFound : i18n.translation.common.error}
        error={error}
      />
      <FlexContent padding noPaddingTop>
        <ErrorText
          text={
            isNotFound(error)
              ? i18n.translation.common.resourceNotFound
              : isCalculationModuleNotSupported(error)
                ? i18n.translation.calculations.errors.moduleNotSupported
                : i18n.translation.common.genericError
          }
        />
      </FlexContent>
    </>
  );
};

export default Calculation;
